import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import {
  RecordVoiceOver,
  VoiceOverOff,
  Close as CloseIcon
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import VoiceAssistantInterface from "../VoiceAssistantInterface";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";

const useStyles = makeStyles((theme) => ({
  assistantDialog: {
    "& .MuiDialog-paper": {
      minWidth: "800px",
      minHeight: "600px",
      maxWidth: "90vw",
      maxHeight: "90vh"
    }
  },
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  dialogContent: {
    padding: 0,
    height: "500px",
    display: "flex",
    flexDirection: "column"
  },
  noAssistantContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    textAlign: "center",
    padding: theme.spacing(4)
  },
  noAssistantIcon: {
    fontSize: 64,
    color: theme.palette.grey[400],
    marginBottom: theme.spacing(2)
  },
  statusChip: {
    marginLeft: theme.spacing(1)
  }
}));

const VoiceAssistantButton = ({ ticket, contact }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [assistants, setAssistants] = useState([]);
  const [activeAssistant, setActiveAssistant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [assistantActive, setAssistantActive] = useState(false);

  // Check permissions and load assistants
  useEffect(() => {
    const checkPermissionsAndLoadAssistants = async () => {
      try {
        // Check if user has permission to use voice assistants
        // This would typically come from user permissions or company settings
        if (!user?.profile) {
          setHasPermission(false);
          setLoading(false);
          return;
        }
        
        const hasVoiceAssistantPermission = user.profile === "admin" || user.profile === "user";
        setHasPermission(hasVoiceAssistantPermission);

        if (hasVoiceAssistantPermission && ticket.status === "open" && ticket.queueId) {
          // Load available assistants for this queue
          setLoading(true);
          const { data } = await api.get(`/assistants?active=true`);
          const availableAssistants = data.assistants || [];
          setAssistants(availableAssistants);
          
          // Filter for active voice assistants and set the first one as default
          const voiceAssistants = availableAssistants.filter(a => 
            a.type === "voice" && 
            a.isActive && 
            a.queueId === ticket.queueId
          );
          
          if (voiceAssistants && voiceAssistants.length > 0) {
            setActiveAssistant(voiceAssistants[0]);
          } else {
            setActiveAssistant(null);
          }
        } else {
          setActiveAssistant(null);
        }
      } catch (error) {
        console.error("Error checking permissions or loading assistants:", error);
        // Don't show error toast for common expected errors
        const status = error.response?.status;
        if (status !== 403 && status !== 404 && status !== 401) {
          toastError(error);
        }
        // Gracefully handle errors
        setHasPermission(false);
        setActiveAssistant(null);
        setAssistants([]);
      } finally {
        setLoading(false);
      }
    };

    if (ticket.id) {
      checkPermissionsAndLoadAssistants();
    }
  }, [ticket.id, ticket.queueId, ticket.status, user?.profile]);

  // Handle opening voice assistant
  const handleOpenVoiceAssistant = useCallback(() => {
    if (!activeAssistant) {
      toastUtils.warning(i18n.t("talkAi.buttons.noAssistantAvailable"), "Assistente Indisponível");
      return;
    }
    setIsOpen(true);
  }, [activeAssistant]);

  // Handle closing voice assistant
  const handleCloseVoiceAssistant = useCallback(() => {
    setIsOpen(false);
    setConnectionStatus("disconnected");
    setAssistantActive(false);
  }, []);

  // Handle connection status change
  const handleConnectionStatusChange = useCallback((status) => {
    setConnectionStatus(status);
  }, []);

  // Handle active state change
  const handleActiveStateChange = useCallback((active) => {
    setAssistantActive(active);
  }, []);

  // Don't render if no permission, ticket is not open
  if (!hasPermission || ticket.status !== "open") {
    return null;
  }
  
  // Don't render if no assistants available (but don't return null while loading)
  if (!loading && !activeAssistant) {
    return null;
  }

  // Render loading state
  if (loading) {
    return (
      <IconButton disabled>
        <Tooltip title={i18n.t("talkAi.buttons.loadingAssistant")}>
          <CircularProgress size={20} />
        </Tooltip>
      </IconButton>
    );
  }

  // Get status color for the button
  const getButtonColor = () => {
    if (assistantActive) return "primary";
    if (connectionStatus === "connected") return "secondary";
    return "default";
  };

  // Get tooltip text
  const getTooltipText = () => {
    if (assistantActive) return i18n.t("talkAi.buttons.voiceAssistantActive");
    if (connectionStatus === "connected") return i18n.t("talkAi.buttons.voiceAssistantConnected");
    return i18n.t("talkAi.buttons.startVoiceAssistant");
  };

  return (
    <>
      <IconButton
        className="MuiIconButton-root MuiIconButton-sizeSmall"
        style={{
          padding: 1,
          color: assistantActive ? "#1976d2" : (connectionStatus === "connected" ? "#f50057" : "#666")
        }}
        onClick={handleOpenVoiceAssistant}
        disabled={loading}
      >
        <Tooltip title={getTooltipText()}>
          {assistantActive ? <RecordVoiceOver /> : <VoiceOverOff />}
        </Tooltip>
      </IconButton>

      {/* Voice Assistant Dialog */}
      <Dialog
        open={isOpen}
        onClose={handleCloseVoiceAssistant}
        className={classes.assistantDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className={classes.dialogHeader}>
          <Box display="flex" alignItems="center">
            <RecordVoiceOver style={{ marginRight: 8 }} />
            <Typography variant="h6">
              Assistente de Voz - {activeAssistant?.name}
            </Typography>
            <Chip
              size="small"
              className={classes.statusChip}
              color={connectionStatus === "connected" ? "primary" : "default"}
              label={
                connectionStatus === "connected" ? i18n.t("voiceAssistants.streaming.connected") :
                connectionStatus === "connecting" ? i18n.t("voiceAssistants.streaming.connecting") :
                i18n.t("voiceAssistants.streaming.disconnected")
              }
            />
          </Box>
          <IconButton onClick={handleCloseVoiceAssistant}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent className={classes.dialogContent}>
          {activeAssistant && user?.companyId ? (
            <VoiceAssistantInterface
              ticketId={ticket.id}
              companyId={user.companyId}
              assistantId={activeAssistant.id}
              onConnectionStatusChange={handleConnectionStatusChange}
              onActiveStateChange={handleActiveStateChange}
              onClose={handleCloseVoiceAssistant}
            />
          ) : (
            <Box className={classes.noAssistantContainer}>
              <VoiceOverOff className={classes.noAssistantIcon} />
              <Typography variant="h6" gutterBottom>
                {i18n.t("talkAi.buttons.noAssistantAvailable")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("talkAi.buttons.configureAssistants")}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VoiceAssistantButton;